const scriptRequests = new Map<string, Promise<HTMLScriptElement>>();

const getSdkUrl = (environment: string): string =>
  `https://html5.inthegame.io/${environment}/main.js`;

const getOmidSdkUrls = (environment: string): string[] => [
  `https://html5.inthegame.io/${environment}/omsdk/omweb-v1.js`,
  `https://html5.inthegame.io/${environment}/omsdk/omid-session-client-v1.js`,
];

const loadScript = (src: string): Promise<HTMLScriptElement> => {
  const existingRequest = scriptRequests.get(src);
  if (existingRequest) return existingRequest;

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[src="${src}"]`,
  );

  if (existingScript?.dataset.loaded === 'true') {
    return Promise.resolve(existingScript);
  }

  const request = new Promise<HTMLScriptElement>((resolve, reject) => {
    const script = existingScript || document.createElement('script');

    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      script.removeEventListener('abort', handleAbort);
    };
    const handleLoad = () => {
      cleanup();
      script.dataset.loaded = 'true';
      resolve(script);
    };
    const handleError = () => {
      cleanup();
      scriptRequests.delete(src);
      reject(new Error(`Failed to load the In The Game SDK from ${src}`));
    };
    const handleAbort = () => {
      cleanup();
      scriptRequests.delete(src);
      reject(new Error(`Loading the In The Game SDK was aborted`));
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    script.addEventListener('abort', handleAbort);

    if (!existingScript) {
      // Download in parallel but execute in insertion order, so dependencies
      // (OMID) run before the scripts that use them.
      script.async = false;
      script.src = src;
      document.head.appendChild(script);
    }
  });

  scriptRequests.set(src, request);
  return request;
};

export const loadSdk = (environment: string): Promise<HTMLScriptElement> => {
  // OMID is optional: a failed load should not block the SDK.
  getOmidSdkUrls(environment).forEach((src) =>
    loadScript(src).catch(() => undefined),
  );
  return loadScript(getSdkUrl(environment));
};

export const unloadSdks = (): void => {
  try {
    window.inthegame?.stop();
  } catch {
    // The third-party SDK can throw while it is already stopping.
  }

  Array.from(
    document.querySelectorAll<HTMLScriptElement>(
      'script[src^="https://html5.inthegame.io/"]',
    ),
  ).forEach((script) => script.remove());
  scriptRequests.clear();
};
